import { Link } from "@tanstack/react-router";
import { Sparkles, ArrowUpRight } from "lucide-react";
import { AI_ANSWERS } from "@/content/ai-answers";

/**
 * Prominent internal link module pointing to the /ai-answers hub.
 * Rendered on city, service, and industry pages. Optionally deep-links
 * to the most relevant questions on the /ai-answers hub via #slug
 * anchors so LLMs and users land directly on the quotable answer.
 */
export function AiAnswersCallout({
  context,
  questionSlugs,
}: {
  /** Short phrase describing the surrounding page, e.g. "Dhaka SEO" */
  context?: string;
  /** Slugs from AI_ANSWERS to feature as deep-link anchors. */
  questionSlugs?: string[];
}) {
  const featured = (questionSlugs ?? [])
    .map((slug) => AI_ANSWERS.find((a) => a.slug === slug))
    .filter((a): a is (typeof AI_ANSWERS)[number] => Boolean(a));
  return (
    <div className="my-16">
      <div className="rounded-3xl border border-primary/30 bg-gradient-to-br from-primary/10 via-card to-card p-8 md:p-10">
        <div className="flex flex-wrap items-center gap-6 justify-between">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-primary">
              <Sparkles size={14} /> AI Answers hub
            </div>
            <h3 className="mt-3 font-display text-2xl md:text-3xl">
              Ask ChatGPT, Perplexity, or Gemini{context ? ` about ${context}` : ""}?
            </h3>
            <p className="mt-3 text-sm text-muted-foreground leading-relaxed">
              24 citation-ready answers on AEO, GEO, schema, local SEO, and
              Bangladesh search — written in the 40–80 word format LLMs quote.
            </p>
          </div>
          <Link
            to="/ai-answers"
            className="group inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-5 py-2.5 text-sm font-medium hover:gap-3 transition-all"
          >
            Browse AI Answers <ArrowUpRight size={14} className="transition-transform group-hover:translate-x-0.5" />
          </Link>
        </div>
        {featured.length > 0 ? (
          <ul className="mt-6 grid gap-2 sm:grid-cols-2">
            {featured.map((a) => (
              <li key={a.slug}>
                <Link
                  to="/ai-answers"
                  hash={a.slug}
                  className="group flex items-start gap-2 rounded-xl border border-border/60 bg-background/40 p-3 text-sm hover:border-primary hover:text-primary transition"
                >
                  <ArrowUpRight size={14} className="mt-0.5 shrink-0 text-primary" />
                  <span className="leading-snug">{a.q}</span>
                </Link>
              </li>
            ))}
          </ul>
        ) : null}
      </div>
    </div>
  );
}